import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Check, ChevronLeft } from 'lucide-react';
import {
    APP_FADE,
    APP_SPRING,
    APP_SPRING_GENTLE,
    revealUpVariants,
    sectionStaggerVariants,
    slideFadeVariants,
    surfaceEntranceVariants,
} from '../utils/motion';

interface OnboardingProps {
    onComplete: () => void;
}

const introSlides = [
    {
        title: 'Convert photos to sketch in seconds',
        desc: 'Turn any gallery image into a clean tracing guide before you start drawing.',
        image: '/onboarding_trace.webp',
    },
    {
        title: 'Use a camera to draw',
        desc: 'Project a live outline onto paper and trace with steady, real-time guidance.',
        image: '/onboarding_camera.webp',
    },
    {
        title: 'Various templates available',
        desc: 'Explore ready-made tracing templates for animals, cars, nature, food, anime, and more.',
        image: '/onboarding_templates.webp',
    },
];

const drawingLevels = [
    { id: 'beginner', label: 'Beginner', image: '/level_beginner.webp' },
    { id: 'novice', label: 'Novice', image: '/level_novice.webp' },
    { id: 'intermediate', label: 'Intermediate', image: '/level_intermediate.webp' },
    { id: 'skilled', label: 'Skilled', image: '/level_skilled.webp' },
];

export const Onboarding: React.FC<OnboardingProps> = ({ onComplete }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [level, setLevel] = useState<string | null>(null);
    const prefersReducedMotion = useReducedMotion();

    const totalSteps = introSlides.length + 1;

    const handleNext = () => {
        if (currentStep < totalSteps - 1) {
            if (currentStep === introSlides.length && !level) return;
            setCurrentStep((curr) => curr + 1);
            return;
        }

        localStorage.setItem(
            'userPreferences',
            JSON.stringify({
                level,
                mediums: [],
                interests: [],
            }),
        );
        onComplete();
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep((curr) => curr - 1);
        }
    };

    const contentVariants = prefersReducedMotion
        ? {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            exit: { opacity: 0 },
        }
        : slideFadeVariants;

    const renderIntroSlide = (index: number) => (
        <motion.div
            variants={sectionStaggerVariants}
            initial="initial"
            animate="animate"
            className="flex w-full flex-col items-center px-4 text-center"
        >
            <motion.div
                variants={surfaceEntranceVariants}
                className="relative mb-8 w-full overflow-hidden rounded-[40px] bg-gray-50 shadow-[0_28px_90px_rgba(99,102,241,0.14)]"
            >
                <motion.div
                    aria-hidden="true"
                    animate={prefersReducedMotion ? undefined : { scale: [1, 1.03, 1], opacity: [0.16, 0.24, 0.16] }}
                    transition={prefersReducedMotion ? undefined : { duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                    className="pointer-events-none absolute inset-x-6 bottom-0 h-24 rounded-full bg-linear-to-r from-indigo-400/20 via-violet-400/22 to-fuchsia-400/18 blur-2xl"
                />
                <div className="aspect-3/4 max-h-[50vh] overflow-hidden">
                    <img
                        src={introSlides[index].image}
                        alt={introSlides[index].title}
                        className="h-full w-full object-cover"
                        width={800}
                        height={800}
                        loading={index === 0 ? 'eager' : 'lazy'}
                        decoding="async"
                        fetchPriority={index === 0 ? 'high' : 'auto'}
                    />
                </div>
            </motion.div>

            <motion.div variants={sectionStaggerVariants} className="mx-auto max-w-xs space-y-3">
                <motion.h2
                    variants={revealUpVariants}
                    className="font-display text-3xl font-bold tracking-tight text-gray-900"
                >
                    {introSlides[index].title}
                </motion.h2>
                <motion.p
                    variants={revealUpVariants}
                    className="text-base leading-7 text-gray-500"
                >
                    {introSlides[index].desc}
                </motion.p>
            </motion.div>
        </motion.div>
    );

    const renderLevelQuestion = () => (
        <motion.div
            variants={sectionStaggerVariants}
            initial="initial"
            animate="animate"
            className="w-full px-6"
        >
            <motion.h2
                variants={revealUpVariants}
                className="mb-3 text-center text-3xl font-bold text-gray-900"
            >
                What&apos;s your drawing level?
            </motion.h2>
            <motion.p
                variants={revealUpVariants}
                className="mx-auto mb-8 max-w-sm text-center text-sm leading-6 text-gray-500"
            >
                We&apos;ll tailor your first templates and guidance to match your comfort level.
            </motion.p>

            <motion.div variants={sectionStaggerVariants} className="grid grid-cols-2 gap-4">
                {drawingLevels.map((lvl) => (
                    <motion.button
                        key={lvl.id}
                        type="button"
                        variants={revealUpVariants}
                        whileTap={{ scale: 0.97 }}
                        whileHover={prefersReducedMotion ? undefined : { y: -3 }}
                        onClick={() => setLevel(lvl.id)}
                        className={`group relative flex aspect-square flex-col items-center justify-center gap-3 overflow-hidden rounded-3xl border-2 transition-all duration-300 ${
                            level === lvl.id
                                ? 'border-indigo-500 bg-indigo-50 shadow-[0_18px_50px_rgba(99,102,241,0.16)]'
                                : 'border-gray-100 bg-white hover:border-indigo-100 hover:shadow-lg'
                        }`}
                    >
                        {level === lvl.id && (
                            <motion.span
                                layoutId="level-selection-glow"
                                className="absolute inset-0 rounded-3xl bg-linear-to-br from-indigo-500/8 to-fuchsia-500/10"
                                transition={APP_SPRING}
                            />
                        )}
                        <div className="relative z-10 flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-white shadow-sm">
                            <img src={lvl.image} alt={lvl.label} className="h-full w-full object-cover opacity-90 transition-opacity group-hover:opacity-100" />
                        </div>
                        <span className={`relative z-10 text-lg font-semibold ${level === lvl.id ? 'text-indigo-700' : 'text-gray-700'}`}>
                            {lvl.label}
                        </span>
                        {level === lvl.id && (
                            <motion.div
                                initial={{ scale: 0.7, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={APP_SPRING}
                                className="absolute right-3 top-3 rounded-full bg-indigo-500 p-1.5 text-white shadow-xs"
                            >
                                <Check size={14} strokeWidth={3} />
                            </motion.div>
                        )}
                    </motion.button>
                ))}
            </motion.div>
        </motion.div>
    );

    return (
        <div className="fixed inset-0 z-50 isolate flex flex-col items-center justify-between overflow-hidden bg-white py-6 font-sans">
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <motion.div
                    key={`onboarding-glow-${currentStep}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={APP_FADE}
                    className={`absolute inset-x-0 top-0 h-64 blur-3xl ${
                        currentStep === totalSteps - 1
                            ? 'bg-[radial-gradient(circle_at_top,rgba(99,102,241,0.18),transparent_58%)]'
                            : 'bg-[radial-gradient(circle_at_top,rgba(129,140,248,0.18),transparent_58%)]'
                    }`}
                />
                <div className="absolute inset-x-0 bottom-0 h-44 bg-[radial-gradient(circle_at_bottom,rgba(236,72,153,0.10),transparent_60%)]" />
            </div>

            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={APP_FADE}
                className="relative z-10 flex min-h-[48px] w-full items-center justify-between px-6"
            >
                {currentStep > 0 ? (
                    <motion.button
                        type="button"
                        onClick={handleBack}
                        whileTap={{ scale: 0.92 }}
                        whileHover={prefersReducedMotion ? undefined : { x: -2 }}
                        className="-ml-2 rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900"
                    >
                        <ChevronLeft size={28} />
                    </motion.button>
                ) : (
                    <div />
                )}

                <motion.button
                    type="button"
                    onClick={() => onComplete()}
                    whileTap={{ scale: 0.96 }}
                    className="px-4 py-2 text-base font-medium text-gray-400 transition-colors hover:text-indigo-600"
                >
                    Skip
                </motion.button>
            </motion.div>

            <div
                className={`relative z-10 flex flex-1 w-full max-w-md flex-col items-center overflow-y-auto py-4 no-scrollbar ${
                    currentStep < introSlides.length ? 'justify-center' : 'justify-start'
                }`}
            >
                <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                        key={currentStep}
                        variants={contentVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        className={`w-full ${currentStep < introSlides.length ? 'touch-none' : ''}`}
                        drag={currentStep < introSlides.length ? 'x' : false}
                        dragConstraints={{ left: 0, right: 0 }}
                        dragElastic={0.2}
                        onDragEnd={(_, info) => {
                            const swipeThreshold = 70;
                            if (info.offset.x < -swipeThreshold) {
                                handleNext();
                            } else if (info.offset.x > swipeThreshold) {
                                handleBack();
                            }
                        }}
                    >
                        {currentStep < introSlides.length && renderIntroSlide(currentStep)}
                        {currentStep === introSlides.length && renderLevelQuestion()}
                    </motion.div>
                </AnimatePresence>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={APP_FADE}
                className="relative z-10 w-full max-w-md bg-white px-6 pb-6 pt-2"
            >
                {currentStep < introSlides.length && (
                    <div className="mb-8 flex justify-center gap-2">
                        {introSlides.map((_, idx) => (
                            <motion.div
                                key={idx}
                                animate={{ width: idx === currentStep ? 32 : 8, opacity: idx === currentStep ? 1 : 0.45 }}
                                transition={prefersReducedMotion ? { duration: 0.15 } : APP_SPRING_GENTLE}
                                className={`h-2 rounded-full ${idx === currentStep ? 'bg-indigo-500' : 'bg-gray-200'}`}
                            />
                        ))}
                    </div>
                )}

                <motion.button
                    type="button"
                    onClick={handleNext}
                    disabled={currentStep === introSlides.length && !level}
                    whileTap={currentStep === introSlides.length && !level ? undefined : { scale: 0.98 }}
                    whileHover={
                        prefersReducedMotion || (currentStep === introSlides.length && !level)
                            ? undefined
                            : { y: -2 }
                    }
                    className={`flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-lg font-bold transition-all duration-300 ${
                        currentStep === introSlides.length && !level
                            ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                            : 'bg-indigo-600 text-white shadow-[0_20px_50px_rgba(99,102,241,0.28)] hover:bg-indigo-700'
                    }`}
                >
                    <span>{currentStep === totalSteps - 1 ? 'Get Started' : 'Continue'}</span>
                    {currentStep !== totalSteps - 1 && <ArrowRight size={20} />}
                </motion.button>
            </motion.div>
        </div>
    );
};
